import React from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { SmokeReveal } from "./SmokeReveal";
import { useRegPrices } from "@/lib/useRegPrices";

const ROWS = [
  "Seminar Ticket", "Championship Ticket", "Lucky Draw Coupon", "Event Jersey",
  "Event Singlet", "Muaythai Short", "Medal + Lanyard", "Certificate", "Event Goodies",
];

const PACKAGES = [
  {
    id: "amateur", name: "WPC 3.0 Amateur", tab: "AMATEUR", accent: "#FF00D8",
    tag: "Fighter Registration", note: "Limited to 400 slots only",
    perks: [false, true, true, true, true, false, true, true, false],
    to: "/register/amateur",
  },
  {
    id: "seminar_adult", name: "Adult Seminar", tab: "SEMINAR", accent: "#00F0FF",
    tag: "Adult Clinic · 18+", note: "Limited to 500 participants",
    perks: [true, true, true, true, false, false, false, true, false],
    to: "/register/seminar-adult",
  },
  {
    id: "seminar_kids", name: "Kids Seminar", tab: "KIDS", accent: "#A78BFA",
    tag: "Kids Clinic · Ages 4–11", note: "Includes 1 parent/guardian ticket",
    perks: [true, true, true, true, false, false, false, true, false],
    to: "/register/seminar-kids",
  },
];

const PackageCard = ({ p, price }) => {
  const navigate = useNavigate();
  return (
    <div
      onClick={() => navigate(p.to)}
      className="group relative w-[272px] shrink-0 cursor-pointer overflow-visible rounded-[28px] border border-white/10 bg-gradient-to-b from-white/[0.09] to-white/[0.02] pb-4 backdrop-blur-xl transition-all duration-300 hover:-translate-y-3 hover:scale-[1.03]"
      style={{ boxShadow: `0 0 70px -20px ${p.accent}66, inset 0 1px 0 rgba(255,255,255,0.12)` }}
      data-testid={`package-card-${p.id}`}
    >
      <div
        className="pointer-events-none absolute -inset-px rounded-[28px] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        style={{ boxShadow: `0 24px 80px -12px ${p.accent}aa, 0 0 50px -8px ${p.accent}88`, border: `1px solid ${p.accent}66` }}
      />
      <div className="pointer-events-none absolute inset-x-8 top-0 h-px rounded-full bg-gradient-to-r from-transparent via-white/50 to-transparent transition-opacity duration-300 group-hover:via-white/80" />
      <div
        className="absolute -left-3 top-12 flex h-32 w-7 items-center justify-center rounded-full shadow-lg transition-transform duration-300 group-hover:-translate-y-1 group-hover:scale-110"
        style={{ background: `linear-gradient(180deg, ${p.accent}, ${p.accent}77)`, boxShadow: `0 0 24px ${p.accent}55` }}
      >
        <span className="rotate-180 font-mono-cap text-[9px] tracking-[0.25em] text-void" style={{ writingMode: "vertical-rl" }}>
          {p.tab}
        </span>
      </div>

      <div className="px-6 pt-6">
        <p className="text-center font-mono-cap text-[10px]" style={{ color: p.accent }}>{p.tag}</p>
        <p
          className="mt-3 text-center font-display text-[42px] leading-none transition-transform duration-300 group-hover:scale-110"
          style={{ color: p.accent, textShadow: `0 0 34px ${p.accent}66` }}
          data-testid={`package-price-${p.id}`}
        >
          RM{price}
        </p>
        <p className="mt-2 text-center font-sub text-sm font-bold uppercase tracking-wide text-white">{p.name}</p>

        <div className="my-5 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />

        <ul className="space-y-2">
          {ROWS.filter((_, i) => p.perks[i]).map((label) => (
            <li key={label} className="flex items-center gap-2.5 font-body text-[12px]">
              <Check className="h-3.5 w-3.5 shrink-0" style={{ color: p.accent }} />
              <span className="text-slate-200">{label}</span>
            </li>
          ))}
        </ul>

        {p.note && (
          <p
            className="mx-auto mt-4 w-fit rounded-full border px-3.5 py-1 text-center font-mono-cap text-[10px] tracking-wide"
            style={{ borderColor: `${p.accent}66`, color: p.accent, background: `${p.accent}14`, boxShadow: `0 0 18px -4px ${p.accent}55` }}
          >
            {p.note}
          </p>
        )}

        <div className="mt-5 flex justify-center">
          <button
            onClick={() => navigate(p.to)}
            className="inline-flex items-center gap-2 rounded-full border px-5 py-2.5 font-mono-cap text-[10px] transition-transform hover:scale-105"
            style={{ borderColor: `${p.accent}88`, color: p.accent }}
            data-testid={`package-cta-${p.id}`}
          >
            Register Now <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div
        className="mt-5 flex h-9 items-center justify-center rounded-b-[28px]"
        style={{ background: `linear-gradient(90deg, ${p.accent}, ${p.accent}cc)` }}
      >
        <span className="font-mono-cap text-[10px] tracking-[0.2em] text-void">{p.name}</span>
      </div>
    </div>
  );
};

export const PackagesSection = () => {
  const prices = useRegPrices();
  return (
    <section className="relative z-10 mx-auto max-w-6xl px-6 py-24 sm:px-10" data-testid="packages-section">
      <SmokeReveal className="mx-auto max-w-3xl text-center">
        <p className="font-mono-cap text-[11px] text-neon-pink">26–28 November 2026 · Stadium Titiwangsa, KL</p>
        <h2 className="mt-4 font-display text-4xl leading-[0.95] sm:text-5xl">
          REGISTRATION <span className="gradient-text">PACKAGES</span>
        </h2>
        <p className="mt-4 font-body text-sm text-slate-400 sm:text-base">
          Every package is packed with official WPC 3.0 entitlements — pick yours and register before slots run out.
        </p>
      </SmokeReveal>
      <Reveal delay={0.1} className="mt-4">
        <div className="flex flex-wrap items-stretch justify-center gap-7 pb-4 pt-10">
          {PACKAGES.map((p) => <PackageCard key={p.id} p={p} price={prices[p.id]} />)}
        </div>
      </Reveal>
    </section>
  );
};
